import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form';
import moment from 'moment';
import cns from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { isLearningAssetCourseType } from '../../../../../../shared/utils/LearningAssetUtils';
import {
    ASSET_METADATA_CATEGORIES,
    ASSET_METADATA_LOOKUP,
    LEARNING_ASSET_DURATIONS,
    LEARNING_ASSET_TYPES,
    TYPE_OPTIONS
} from '../../../../../../shared/Constants';
import BasicText from '../../../../../../shared/components/formComponents/groups/BasicText';
import BasicSelect from '../../../../../../shared/components/formComponents/groups/BasicSelect';
import BasicToggle from '../../../../../../shared/components/formComponents/groups/BasicToggle';
import { IMAGE_PROPS } from '../../../../../../shared/utils/ImageUtils';
import RawImageUpload from '../../../../../../shared/components/formComponents/inputs/RawImageUpload';
import BasicTextArea from '../../../../../../shared/components/formComponents/groups/BasicTextArea';
import BasicNumericInput from '../../../../../../shared/components/formComponents/groups/BasicNumericInput';
import BehaviorSelect from '../../../../../../shared/components/formComponents/BehaviorSelect';
import RenderParticipants from '../RenderParticipants';
import LearningAssetStatusPanel from './LearningAssetStatusPanel';
import SearchSelect from '../../../../../../shared/components/SearchSelect';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../../shared/components/VerbButton';

export default class SummaryTab extends PureComponent {
    static propTypes = {
        isVerbAdmin: PropTypes.bool,
        isContentAuthor: PropTypes.bool,
        isContentAdmin: PropTypes.bool,
        dimensions: PropTypes.arrayOf(PropTypes.object),
        behaviors: PropTypes.arrayOf(PropTypes.object),
        abilities: PropTypes.arrayOf(PropTypes.object),
        behaviorsById: PropTypes.object,
        dimensionsById: PropTypes.object,
        abilitiesById: PropTypes.object,
        changeFormValue: PropTypes.func,
        assetTypeOptions: PropTypes.array,
        currentLearningAsset: PropTypes.object,
        currentLearningAssetStatus: PropTypes.object,
        assetType: PropTypes.string,
        participantsDataSource: PropTypes.array,
        loggedInUser: PropTypes.object
    };

    constructor(props) {
        super(props);
        this.state = {};

        this.levelOptions = [
            { label: '1', value: 1 },
            { label: '2', value: 2 },
            { label: '3', value: 3 },
            { label: '4', value: 4 },
            { label: '5', value: 5 }
        ];

        this.scheduleTypes = [
            { label: 'Self Paced', value: 'selfpaced' },
            { label: 'Scheduled', value: 'scheduled' }
        ];
    }

    _searchFormatter = (m) => m && m.join(', ');

    _searchParser = (m) => {
        let terms = [];
        if (m.length) {
            terms = m.split(',').map((term) => term.trim());
        }
        return terms;
    };

    _buildAuthorOptions = (profiles = []) => {
        const { loggedInUser } = this.props;
        const valuesArray = [];
        profiles.forEach((profile) => {
            if (loggedInUser._id !== profile._id) {
                valuesArray.push({
                    value: profile._id,
                    label: `${profile.firstName} ${profile.lastName} - ${profile.email}`,
                    email: profile.email,
                    firstName: profile.firstName,
                    lastName: profile.lastName
                });
            }
        });
        return valuesArray;
    };

    _changeAuthor = (newAuthor) => {
        const { changeFormValue } = this.props;
        const newAuthorObject = { _id: newAuthor.value, firstName: newAuthor.firstName, lastName: newAuthor.lastName };
        changeFormValue({
            field: 'author',
            value: newAuthorObject
        });
        this.setState({
            newAuthor: newAuthorObject,
            isChangingAuthor: false
        });
    };

    _createCollectionList = (associatedCollections = []) => {
        const defaultString = 'one or more an active collections';
        if (associatedCollections.length === 1) {
            return associatedCollections[0]?.name || defaultString;
        }
        if (associatedCollections.length > 1) {
            const collectionNames = associatedCollections.map(({ name }) => name);
            return `${collectionNames.slice(0, collectionNames.length - 1).join(', ')} and ${
                collectionNames[collectionNames.length - 1]
            }`;
        }
        return defaultString;
    };

    render() {
        const {
            isVerbAdmin,
            isContentAdmin,
            dimensions,
            behaviors,
            abilities,
            behaviorsById,
            dimensionsById,
            abilitiesById,
            changeFormValue,
            assetTypeOptions,
            currentLearningAsset,
            currentLearningAssetStatus,
            currentLearningAssetStatus: { isInCollection = false, associatedCollections = [] },
            assetType,
            participantsDataSource
        } = this.props;

        const { newAuthor, isChangingAuthor } = this.state;
        const isHidden = isContentAdmin && !isVerbAdmin;
        const isCourseType = isLearningAssetCourseType(assetType);
        const durationOptions = Object.values(LEARNING_ASSET_DURATIONS());
        const isFeedbackType = assetType === LEARNING_ASSET_TYPES.FEEDBACK;
        const allowHideQuestionAnswerHeader = [LEARNING_ASSET_TYPES.REFLECTION, LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION, LEARNING_ASSET_TYPES.VIDEO, LEARNING_ASSET_TYPES.SINGLE_PAGE_ACTIVITY, LEARNING_ASSET_TYPES.ACTIVITY].includes(assetType);
        const metadataSelectOptions = Object.values(ASSET_METADATA_LOOKUP).map((tag) => ({
            ...tag,
            label: `${ASSET_METADATA_CATEGORIES[tag.categoryId].label}: ${tag.label}`
        }));

        return (
            <div className={'trackSummary row'}>
                <div className={'col-3 left-panel tab-spacing'}>
                    <div className={'row'}>
                        <Field
                            name={'name'}
                            className={'col-12'}
                            label={'Name'}
                            maxLength={'60'}
                            component={BasicText}
                            displayHelp={true}
                        />
                    </div>
                    <div className={'row'}>
                        <Field
                            name={'type'}
                            label={'Activity Type'}
                            component={BasicSelect}
                            className={'col-9'}
                            displayHelp={true}
                            options={assetTypeOptions}
                            onChange={this._handleAssetTypeOnChange}
                        />
                        {!isHidden ? (
                            <div className={'row'}>
                                <OverlayTrigger
                                    trigger={isInCollection && 'hover'}
                                    key={'left'}
                                    placement={'left'}
                                    overlay={
                                        <Tooltip
                                            id={`tooltip-left`}
                                        >{`Unable to deactivate Activity as it is still a part of ${this._createCollectionList(
                                            associatedCollections
                                        )}`}</Tooltip>
                                    }
                                >
                                    <div className={'row is-active summary-active-toggle-row'}>
                                        <Field
                                            name={'isActive'}
                                            label={'Active?'}
                                            disabled={isInCollection}
                                            className={'col-3 is-active'}
                                            component={BasicToggle}
                                            id={'summary-is-active-toggle'}
                                            inputClassName={'col-3'}
                                        />
                                    </div>
                                </OverlayTrigger>
                            </div>
                        ) : null}
                    </div>
                    <div className={'row'}>
                        <Field
                            name={'typeLabel'}
                            label={'Label'}
                            component={BasicSelect}
                            isClearable={true}
                            clearValue={''}
                            className={'col-9'}
                            displayHelp={true}
                            options={TYPE_OPTIONS}
                        />
                    </div>
                    <div className={'row'}>
                        {!isHidden ? (
                            <>
                                <Field
                                    name={'allowResubscribe'}
                                    label={'Collaborative Activity'}
                                    className={'col-12 allow-resubscribe'}
                                    component={BasicToggle}
                                    id={'allow-resubscribe-toggle'}
                                />
                            </>
                        ) : null}
                    </div>
                    <LearningAssetStatusPanel currentLearningAssetStatus={currentLearningAssetStatus} />
                    {currentLearningAsset.author && !isChangingAuthor ? (
                        <div className={'row info-row'}>
                            <div className={'col-12'}>
                                ORIGINAL AUTHOR:
                                {!isEmpty(newAuthor)
                                    ? ` ${newAuthor.firstName} ${newAuthor.lastName}`
                                    : ` ${currentLearningAsset.author.firstName} ${currentLearningAsset.author.lastName}`}
                                &nbsp; &nbsp;
                                {isVerbAdmin || isContentAdmin ? (
                                    <VerbButton
                                        buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                                        fontAwesomeIcon={'fa-pencil'}
                                        onClick={() => {
                                            this.setState({
                                                isChangingAuthor: true
                                            });
                                        }}
                                    />
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                    {isChangingAuthor ? (
                        <div className={'row info-row'}>
                            <div className={'col-8'}>
                                ORIGINAL AUTHOR:
                                <SearchSelect
                                    formOptions={this._buildAuthorOptions}
                                    indexName={'Profile'}
                                    className={'col-12'}
                                    placeholder={'Enter name or email'}
                                    defaultFilters={''}
                                    attributeList={['_id', 'firstName', 'lastName', 'email']}
                                    handleSelect={this._changeAuthor}
                                />
                            </div>
                        </div>
                    ) : null}
                    {currentLearningAsset.updatedBy ? (
                        <div className={'row info-row'}>
                            <div className={'col-12'}>
                                LAST UPDATED BY: {currentLearningAsset.updatedBy.firstName}{' '}
                                {currentLearningAsset.updatedBy.lastName}
                            </div>
                        </div>
                    ) : null}
                    {currentLearningAsset.updatedOn ? (
                        <div className={'row info-row'}>
                            <div className={'col-12'}>
                                UPDATED ON: {moment(currentLearningAsset.updatedOn).format('MMMM Do YYYY')}
                            </div>
                        </div>
                    ) : null}
                    <div className={'row thumbnail-image-row'}>
                        <Field
                            name={'thumbnailImageUrl'}
                            label={'Thumbnail Image'}
                            className={'col-12'}
                            imageProps={IMAGE_PROPS.learningAssetImageCloudinary}
                            squareImage={true}
                            component={RawImageUpload}
                            inputClassName={'mx-auto'}
                        />
                    </div>
                </div>
                <div className={'col-9 right-panel'}>
                    <div className={'row'}>
                        <Field
                            name={'subtitle'}
                            label={'Subtitle'}
                            className={'col-8'}
                            maxLength={'125'}
                            component={BasicTextArea}
                            rows={2}
                        />
                    </div>
                    <div className={'row'}>
                        {/* this._renderCategoriesSelect() */}
                        <Field
                            name={'isImpact'}
                            label={'Impact Activity?'}
                            className={'col-2'}
                            component={BasicToggle}
                            disabled={isFeedbackType}
                            id={'is-impact-toggle'}
                        />
                        <Field
                            name={'hideQuestionAndResultsHeaders'}
                            label={'Hide Question/Result Headers?'}
                            className={cns('col-2', { hidden: !allowHideQuestionAnswerHeader })}
                            component={BasicToggle}
                            disabled={!allowHideQuestionAnswerHeader}
                            id={'is-question-results-header-toggle'}
                        />
                        <Field
                            name={'openNavigation'}
                            label={'Allow Open Navigation'}
                            className={cns('col-2', { hidden: !isCourseType })}
                            component={BasicToggle}
                            id={'open-navigation-toggle'}
                        />
                    </div>
                    <div className={'row'}>
                        <Field
                            name={'durationId'}
                            label={'Duration'}
                            className={'col-2'}
                            isClearable={true}
                            options={durationOptions}
                            component={BasicSelect}
                        />
                        <Field
                            name={'scheduleType'}
                            label={'Schedule'}
                            className={'col-2 hidden'}
                            options={this.scheduleTypes}
                            component={BasicSelect}
                        />
                        <Field
                            name={'level'}
                            label={'Level'}
                            className={'col-2'}
                            options={this.levelOptions}
                            component={BasicSelect}
                        />
                        {!isHidden && (
                            <>
                                <Field
                                    name={'videoMinutes'}
                                    label={'Video Minutes'}
                                    className={'col-2'}
                                    minValue={0}
                                    component={BasicNumericInput}
                                />
                                <Field
                                    name={'audioMinutes'}
                                    label={'Audio Minutes'}
                                    className={'col-2'}
                                    minValue={0}
                                    component={BasicNumericInput}
                                />
                            </>
                        )}
                    </div>
                    <div className={'row'}>
                        <BehaviorSelect
                            behaviorsById={behaviorsById}
                            dimensionsById={dimensionsById}
                            dimensions={dimensions}
                            abilities={abilities}
                            abilitiesById={abilitiesById}
                            behaviors={behaviors}
                            changeFormValue={changeFormValue}
                        />
                    </div>
                    <div className={'row'}>
                        <Field
                            name={'coverImageUrl'}
                            label={'Cover Image'}
                            className={cns('col-6', { hidden: !isCourseType })}
                            component={RawImageUpload}
                            inputClassName={'mx-auto'}
                        />
                    </div>
                    {!isHidden && (
                        <div className={'row'}>
                            <Field
                                name={'adminDescription'}
                                label={'Admin Description'}
                                maxLength={'1000'}
                                component={BasicTextArea}
                                className={'col-12'}
                            />
                        </div>
                    )}
                    <div className={'row admin-description'}>
                        <Field
                            name={'searchTerms'}
                            label={'Search Terms - Separate By Commas'}
                            component={BasicTextArea}
                            rows={2}
                            parse={this._searchParser}
                            format={this._searchFormatter}
                            className={'col-12'}
                        />
                    </div>
                    <div className={'row metadata-tags'}>
                        <Field
                            className={'col-12'}
                            name={'metadataTags'}
                            label={'Activity Metadata'}
                            options={metadataSelectOptions}
                            isMulti={true}
                            valueKey={'id'}
                            labelKey={'label'}
                            component={BasicSelect}
                        />
                    </div>
                    <div className={'row'}>
                        <div className={'col-8'}>
                            {isCourseType || assetType === 'FEEDBACK' ? (
                                <FieldArray
                                    name={'participants'}
                                    type={assetType}
                                    component={RenderParticipants}
                                    participantsDataSource={participantsDataSource}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
